import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Suffolk has a RED DfT rating — does that guarantee my claim will succeed?',
    answer: 'No rating guarantees an outcome. The Department for Transport awarded Suffolk County Council RED overall (condition AMBER, spend RED, best practice AMBER). That signals network-level pressure — but Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Your photos, prior reports and the road\'s place in Suffolk\'s survey cycle matter more than the headline badge.',
  },
  {
    question: 'Doesn\'t Suffolk performing better than the national average hurt my claim?',
    answer: 'Not automatically. Suffolk\'s 2023 SCANNER data beat national RED averages on classified roads (2.3% A-road RED vs 4% nationally). Section 58 requires a reasonable maintenance system for your specific defect, not performance relative to other councils. Moreover, 2024 data shows classified roads deteriorating — A-road green condition fell from 71.9% to 67.5% and amber rose to 30.0%, the highest in five years — while U-road condition data for 2024/25 is unavailable due to a methodology switch covering 51.7% of the network.',
  },
  {
    question: 'What if my pothole was on a U-road with no 2024 survey data?',
    answer: 'Unclassified roads make up 3,467km — 51.7% of Suffolk\'s 6,701km carriageway network. The council\'s June 2025 transparency report states that during the 2024/25 survey year SCC transitioned to an alternative survey methodology, so U-road condition data for that year is not available; outputs resume in 2025/26. If your incident was on a U-road during that gap, the council\'s published network-level condition evidence may not cover your street at all.',
  },
  {
    question: 'Suffolk\'s pothole prevention programme claims 6,077 potential potholes rectified — does that block Section 58?',
    answer: 'Not on its own. The winter 2024/25 phase of the Flattening the Curve programme recorded 3,519 repairs and claims 6,077 potential potholes rectified — yet the council still filled 19,159 carriageway potholes in 2024/25, virtually unchanged from 19,131 in 2023/24. A prevention programme that runs alongside ~19,000 annual reactive fills does not prove your specific defect was known and repaired within inspection intervals.',
  },
  {
    question: 'Why does 30% of A-roads in amber condition matter for my claim?',
    answer: 'Amber means maintenance may be required soon. At 30.0% in 2024 — up from 25.8% in 2023 and the highest in five years — nearly one-third of Suffolk\'s 634km of A-roads were deteriorating towards red. The council plans to resurface around 19.33km (12 miles) of carriageways in 2025/26. That scale of backlog relative to planned treatment is evidence of systemic pressure on the network, which can support an argument about inadequate preventative capacity — especially if your defect formed on a road class showing accelerating deterioration.',
  },
  {
    question: 'Suffolk spent 60% reactively in 2023/24 — what does that mean for claims?',
    answer: 'The transparency report shows preventative maintenance fell to 40% and reactive rose to 60% in 2023/24 — the most reactive year in the published table. Classified A-road green condition then fell sharply in 2024 (from 71.9% to 67.5%). Reactive firefighting does not excuse a specific unrepaired defect, but it helps explain why deterioration accelerated after years of improvement funded by enhanced investment that ended — and why ~19,000 potholes per year persist despite prevention programmes.',
  },
  {
    question: 'How do I report a pothole to Suffolk County Council?',
    answer: 'Report highways defects via Suffolk\'s Highways Reporting Tool at highwaysreporting.suffolk.gov.uk, or through the council\'s report-a-highways-issue page. Do not use the online tool for immediate dangers — call 0345 606 6171 instead. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function SuffolkCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Suffolk Pothole Claims | 30% A-Roads in Amber, 19,159 Repairs | Fixtyer"
        description="Suffolk County Council holds a RED DfT rating. A-road amber condition hit 30% in 2024, U-road survey data is missing for 2024/25, and the council still filled 19,159 potholes in 2024/25. What that means for your Section 58 claim."
        canonical="/councils/suffolk"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Suffolk', url: '/councils/suffolk' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Suffolk County Council"
        councilSlug="suffolk"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/suffolk"
        headline="Suffolk Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Suffolk</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            30% of A-Roads in Amber as Suffolk Fills 19,159 Potholes in 2024/25
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Suffolk County Council earns a <strong className="text-rose-400">RED</strong> overall DfT rating — condition{' '}
            <strong className="text-amber-400">AMBER</strong>, spend <strong className="text-rose-400">RED</strong>, best
            practice <strong className="text-amber-400">AMBER</strong>. After a decade of improvement funded by £21 million
            additional investment (2017–2021), classified roads are reversing: A-road green condition fell from 82.1% in
            2021 to <strong className="text-rose-400">67.5% in 2024</strong>, with amber at{' '}
            <strong className="text-amber-400">30.0%</strong> — the highest in five years. Meanwhile{' '}
            <strong className="text-rose-400">51.7% of the network</strong> has no U-road condition data for 2024/25 after
            a methodology switch, and the council still filled <strong className="text-rose-400">19,159 carriageway potholes</strong>{' '}
            in 2024/25. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">30.0%</div>
            <div className="text-white font-medium mt-2">Of A-roads in amber condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 25.8% in 2023 and the highest in five years — on a 634km A-road network where Suffolk plans to
              resurface just 19.33km (12 miles) in 2025/26, while still filling ~19,000 potholes annually.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            6,701km of Roads — Half Are Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Suffolk County Council&apos;s June 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">6,701km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">3,467km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">51.7% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">2,600km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">634km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">9.5% of carriageway network</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Suffolk highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">10,032km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">5,700km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">337km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">1,979</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Suffolk County Council manages and maintains adopted highway covering a length of over 6,700km. Within this adopted highway network, we maintain over 10,000km of footways and 337km of cycleways."
            source="Suffolk County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Classified Roads Deteriorating After a Decade of Improvement
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Suffolk&apos;s transparency report — A, B and C roads only
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> A, B and C roads are surveyed using SCANNER laser technology and
              reported annually. U-roads were surveyed using AI imagery analysis — but during the 2024/25 survey year
              SCC transitioned to an alternative methodology, so U-road condition data for that year is not available.
              Outputs resume in 2025/26. From 2026/27, PAS 2161 will replace the current three-band red/amber/green
              system nationally. Survey snapshots also may not reflect condition at the time of your incident.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (634km) — surveyed 100% annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.5%</td>
                    <td className="px-4 py-2 text-center">19.4%</td>
                    <td className="px-4 py-2 text-center">79.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.2%</td>
                    <td className="px-4 py-2 text-center">16.7%</td>
                    <td className="px-4 py-2 text-center text-emerald-600 font-medium">82.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">23.5%</td>
                    <td className="px-4 py-2 text-center">74.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">25.8%</td>
                    <td className="px-4 py-2 text-center">71.9%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">2.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">30.0%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">67.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Green A-road condition fell 14.6 percentage points from the 2021 peak of 82.1% to 67.5% in 2024. Amber
              rose to 30.0% — the highest in five years — meaning nearly one-third of principal roads may require
              maintenance soon.
            </p>
          </div>

          {/* B and C roads */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-4">B roads — gradual amber increase</h3>
              <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-100 border-b border-surface-200">
                      <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                      <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                      <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                      <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-surface-100">
                      <td className="px-4 py-2 font-mono">2023</td>
                      <td className="px-4 py-2 text-center">2.6%</td>
                      <td className="px-4 py-2 text-center">20.8%</td>
                      <td className="px-4 py-2 text-center">76.6%</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-mono font-bold">2024</td>
                      <td className="px-4 py-2 text-center font-bold">2.6%</td>
                      <td className="px-4 py-2 text-center font-bold text-amber-600">21.5%</td>
                      <td className="px-4 py-2 text-center font-bold">75.8%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-4">C roads — red rising in 2024</h3>
              <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-100 border-b border-surface-200">
                      <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                      <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                      <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                      <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-surface-100">
                      <td className="px-4 py-2 font-mono">2023</td>
                      <td className="px-4 py-2 text-center">3.8%</td>
                      <td className="px-4 py-2 text-center">23.7%</td>
                      <td className="px-4 py-2 text-center">72.5%</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="px-4 py-2 font-mono font-bold">2024</td>
                      <td className="px-4 py-2 text-center font-bold text-rose-600">4.8%</td>
                      <td className="px-4 py-2 text-center font-bold">24.4%</td>
                      <td className="px-4 py-2 text-center font-bold">70.8%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-surface-600 text-sm">
                C-road red condition rose to 4.8% in 2024 — up from 3.8% in 2023 and the highest since 2021.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="During the 2024–25 survey year, SCC transitioned to an alternative survey methodology. As a result, survey data for that year is not available. The new methodology will produce outputs for the 2025–26 survey year."
            source="Suffolk County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* U-road data gap */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            51.7% of the Network: No U-Road Data for 2024/25
          </h2>
          <p className="text-surface-600 mb-8">
            Historical U-road improvement — then a methodology switch the year classified roads deteriorated
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">U-road red condition (% of network) — last published data</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">22.5%</td>
                    <td className="px-4 py-2 text-center">77.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">21.1%</td>
                    <td className="px-4 py-2 text-center">78.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">19.3%</td>
                    <td className="px-4 py-2 text-center">80.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-emerald-600">18.4%</td>
                    <td className="px-4 py-2 text-center">81.6%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-surface-500 italic" colSpan={2}>
                      Data not available — methodology transition
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road red condition improved steadily from 22.5% (2020) to 18.4% (2023) — then data stopped in the
              same survey year A-road amber hit 30.0%. For 3,467km of rural and residential roads, claimants cannot
              cite published network condition at the time of their incident.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Suffolk must show a reasonable system for knowing the condition of
              its roads. For U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6 text-sm">
              <li>• Was your road surveyed before the 2024/25 methodology gap — and when?</li>
              <li>• What condition record exists for your street when 51.7% of the network has no published 2024/25 data?</li>
              <li>• If classified roads deteriorated sharply in 2024, what evidence exists that U-roads did not follow suit?</li>
              <li>• Can the council prove reasonable knowledge of a defect on a road type whose condition data is unavailable?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            RED Spend — Capital Below DfT Allocation
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Projected 2025/26 capital spend of £36.398m against a DfT allocation of £40.833m — explaining the RED spend scorecard
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-3xl font-bold text-rose-400">£40.833m</div>
              <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-rose-400">£36.398m</div>
              <div className="text-brand-300 text-sm">Projected SCC capital spend 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-brand-200">£19.594m</div>
              <div className="text-brand-300 text-sm">Projected revenue spend 2025/26</div>
            </div>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Suffolk highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">SCC capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">40,833</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">36,398</td>
                  <td className="px-4 py-3 text-center">19,594</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">34,428</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">47,885</td>
                  <td className="px-4 py-3 text-center">17,944</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2023/24</td>
                  <td className="px-4 py-3 text-center">32,829</td>
                  <td className="px-4 py-3 text-center">35,182</td>
                  <td className="px-4 py-3 text-center">17,513</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">40%</td>
                  <td className="px-4 py-3 text-center text-rose-400 font-bold">60%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">27,238</td>
                  <td className="px-4 py-3 text-center">40,279</td>
                  <td className="px-4 py-3 text-center">16,395</td>
                  <td className="px-4 py-3 text-center">58%</td>
                  <td className="px-4 py-3 text-center">42%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">27,238</td>
                  <td className="px-4 py-3 text-center">35,268</td>
                  <td className="px-4 py-3 text-center">14,145</td>
                  <td className="px-4 py-3 text-center">49%</td>
                  <td className="px-4 py-3 text-center">51%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">36,829</td>
                  <td className="px-4 py-3 text-center">30,981</td>
                  <td className="px-4 py-3 text-center">15,274</td>
                  <td className="px-4 py-3 text-center">53%</td>
                  <td className="px-4 py-3 text-center">47%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is RED for 2025/26</h3>
              <p className="text-brand-200 text-sm">
                The DfT spend scorecard compares capital spend to allocation. Suffolk projects £36.398m capital against
                £40.833m allocated — spending below the grant. That follows £21 million additional council investment
                (2017–2021) and a further £21 million capital programme (2021–2025) for drainage, footways and signage,
                after which classified road conditions began reversing in 2024.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">2023/24 reactive spike</h3>
              <p className="text-brand-200 text-sm">
                Reactive maintenance reached 60% in 2023/24 — the highest in the published table — while preventative
                work fell to 40%. A-road green condition then dropped sharply in 2024. Planned 2025/26 carriageway
                treatment covers 171.96km (resurfacing plus surface dressing) — 2.57% of the 6,701km network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            ~19,000 Potholes Filled Every Year — No Reduction
          </h2>
          <p className="text-surface-600 mb-8">
            Carriageway pothole fills from Suffolk&apos;s transparency report — reactive workload that persists despite prevention programmes
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Suffolk carriageway potholes filled 2023-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway potholes</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Footway potholes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">19,131</td>
                  <td className="px-6 py-4 text-center">2,869</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19,159</td>
                  <td className="px-6 py-4 text-center font-bold">3,349</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">19,145</td>
                  <td className="px-6 py-4 text-center font-bold">3,109</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Flattening the Curve — winter 2024/25 phase</h3>
              <div className="space-y-2 text-sm text-surface-600">
                <div className="flex justify-between border-b border-surface-200 py-2">
                  <span>Dragon Patching repairs</span>
                  <span className="font-mono font-medium">2,907</span>
                </div>
                <div className="flex justify-between border-b border-surface-200 py-2">
                  <span>Roadmender repairs</span>
                  <span className="font-mono font-medium">612</span>
                </div>
                <div className="flex justify-between border-b border-surface-200 py-2">
                  <span>Total repairs undertaken</span>
                  <span className="font-mono font-medium">3,519</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Potential potholes rectified (claimed)</span>
                  <span className="font-mono font-medium text-emerald-600">6,077</span>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Prevention vs reality</h3>
              <p className="text-amber-800 text-sm">
                The pothole prevention programme addresses defects not yet meeting HMOP safety intervention criteria.
                Yet carriageway pothole fills stayed at ~19,000 in 2024/25 — no reduction from 2023/24. The council
                plans ~3,500 further prevention repairs in 2025/26 while projecting another 19,145 pothole fills.
                Volume of reactive work indicates defects still form continuously across 6,701km.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The pothole prevention programme piloted a proactive approach to road maintenance by combining two repair methods: Roadmender for urban centres and Dragon Patching for rural routes. Both methods were deployed to address potholes not yet meeting safety intervention criteria but projected to worsen if left untreated."
            source="Suffolk County Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Section 58 and claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated County Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Suffolk improved for a decade — but 2024 data shows the trajectory reversing
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 2023 classified-road RED condition below national averages
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Decade of improvement: U-road red fell from 587.7 to 397 miles (2012–2023)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £21 million additional investment 2017–2021 plus £21 million 2021–2025 programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Pothole prevention programme and asset management strategy in place
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 2024/25 capital spend (£47.885m) exceeded DfT allocation (£34.428m)
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall DfT rating — condition AMBER, spend RED, best practice AMBER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road green 67.5% and amber 30.0% in 2024 — worst green, highest amber in five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 3,467km U-roads (51.7%) with no 2024/25 condition data after methodology switch
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 19,159 carriageway potholes filled in 2024/25 — plateau despite prevention programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 60% reactive spending in 2023/24 preceded 2024 classified-road deterioration
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Suffolk must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show
              a reasonable system for inspecting and repairing the specific defect — not point to a decade of
              improvement that ended when enhanced funding ended.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the U-road network during the 2024/25 survey data gap?</li>
              <li>• Did the defect meet HMOP intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (Highways Reporting Tool, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond Suffolk&apos;s inspection interval?</li>
            </ul>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that can cite years of improvement and below-average 2023 RED scores, your claim lives
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 2024/25 survey gap is your strongest structural argument</li>
              <li>• Timing — if your incident followed the 2023/24 reactive spending spike and 2024 deterioration</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Suffolk&apos;s
              own transparency data where it helps you — without pretending the council never improved its roads.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Suffolk County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. For immediate dangers, call{' '}
            <strong>0345 606 6171</strong> rather than using the online tool.
          </p>
          <a
            href="https://www.suffolk.gov.uk/roads-and-transport/highway-maintenance/report-a-highways-issue"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a highways issue — suffolk.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Suffolk?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A reversing improvement story demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 2024/25 data-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No 30% A-road amber deterioration cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road survey gap and 2024 deterioration documented</li>
                <li>✅ RED/AMBER DfT scorecards cited accurately</li>
                <li>✅ 19,159 pothole fills in 2024/25 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Suffolk</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=suffolk"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Suffolk Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.suffolk.gov.uk/asset-library/SCC-Local-highways-maintenance-transparency-report-June-25-Final-V0.0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Suffolk County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
